import React from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { TableHelper } from 'common/components/table-react/types';
import { Experiment, ExperimentStatus } from 'apps/experiments/src/types';
import { LocalizationStore } from 'common/i18n/store';
import { Status, UserInfo } from './cell-renderers';
import styles from './custom-cell-types.module.styl';
import { ExperimentTableCellType } from './types';
export { ExperimentTableCellType };
const STATUS_ORDER = {
  [ExperimentStatus.DRAFT]: 3,
  [ExperimentStatus.ACTIVE]: 2,
  [ExperimentStatus.CONCLUDED]: 1,
  [ExperimentStatus.SUCCESS]: 4,
  [ExperimentStatus.FAIL]: 5
};
export const customCellTypesCallback = <T extends Experiment,>(tableHelper: TableHelper<T>, i18n: LocalizationStore) => {
  return {
    [ExperimentTableCellType.UserInfo]: tableHelper.cellType<string>({
      template: (_, info, params) => {
        var _params$getUserInfo, _params$getUserInfo2;
        const userIconAttrs = (_params$getUserInfo = params === null || params === void 0 || (_params$getUserInfo2 = params.getUserInfo) === null || _params$getUserInfo2 === void 0 ? void 0 : _params$getUserInfo2.call(params, info.cellContext.row)) !== null && _params$getUserInfo !== void 0 ? _params$getUserInfo : {};
        return <UserInfo user={userIconAttrs} />;
      }
    }),
    [ExperimentTableCellType.Status]: tableHelper.cellType<string>({
      sortRankTransform: (_, context) => {
        var _context$row$original, _STATUS_ORDER$status;
        const sortDirection = context.column.getIsSorted();
        const status = ((_context$row$original = context.row.original) === null || _context$row$original === void 0 ? void 0 : _context$row$original.status) satisfies ExperimentStatus;
        return (_STATUS_ORDER$status = STATUS_ORDER[status]) !== null && _STATUS_ORDER$status !== void 0 ? _STATUS_ORDER$status : sortDirection === "asc" ? 6 : 0;
      },
      template: (_, info, params) => {
        var _params$getStatus, _params$getPercent;
        const status = params === null || params === void 0 || (_params$getStatus = params.getStatus) === null || _params$getStatus === void 0 ? void 0 : _params$getStatus.call(params, info.cellContext.row);
        const percent = params === null || params === void 0 || (_params$getPercent = params.getPercent) === null || _params$getPercent === void 0 ? void 0 : _params$getPercent.call(params, info.cellContext.row);
        return <Status status={status} percent={percent} />;
      }
    }),
    [ExperimentTableCellType.Results]: tableHelper.cellType<string>({
      template: (_, info) => {
        const {
          label,
          className
        } = transformResults(i18n, info.cellContext.row.original);
        return <div className={clsx(styles.results, className)} title={label}>
            {label}
          </div>;
      }
    }),
    [ExperimentTableCellType.FormattedDate]: tableHelper.cellType<string>({
      template: (value: string) => {
        return <div className={!value ? styles.dateNa : styles.datePresent}>
            {!value ? i18n.t("experiments.date.na") : i18n.formatDate(moment(value), {
            month: "short",
            day: "numeric",
            year: "numeric"
          })}
          </div>;
      }
    })
  };
};
type TransformResults = {
  label: string;
  className: keyof typeof styles;
};
function transformResults(i18n: LocalizationStore, experiment: Experiment): TransformResults {
  var _experiment$results_c, _experiment$results_c2, _experiment$results_c3, _experiment$results_c4;
  switch (experiment.status) {
    case ExperimentStatus.ACTIVE:
      return {
        label: i18n.t("experiments.results.active"),
        className: styles.resultsNa
      };
    case ExperimentStatus.CONCLUDED:
      return {
        label: i18n.t("experiments.results.concluded"),
        className: styles.resultsNone
      };
    case ExperimentStatus.SUCCESS:
      return {
        label: (_experiment$results_c = (_experiment$results_c2 = experiment.results_cache) === null || _experiment$results_c2 === void 0 ? void 0 : _experiment$results_c2.message) !== null && _experiment$results_c !== void 0 ? _experiment$results_c : "",
        className: styles.resultsSuccess
      };
    case ExperimentStatus.FAIL:
      return {
        label: (_experiment$results_c3 = (_experiment$results_c4 = experiment.results_cache) === null || _experiment$results_c4 === void 0 ? void 0 : _experiment$results_c4.message) !== null && _experiment$results_c3 !== void 0 ? _experiment$results_c3 : "",
        className: styles.resultsFail
      };
    case ExperimentStatus.DRAFT:
    default:
      return {
        label: i18n.t("experiments.results.draft"),
        className: styles.resultsNa
      };
  }
}